import React, { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ReplayIcon from '@mui/icons-material/Replay'
import { hasAuthourity } from '../../../../../lib/access.es6'
import { replayPayment } from '../../../../../lib/requests.es6'
import {
  canReplayPayment,
  replayCount,
  replayReason
} from '../../../../../lib/paymentReplay.es6'

// Same rule as the other replay screens: replaying credits a policy, so the person says why.
const MIN_REASON_LENGTH = 5

const messageOf = error => {
  const data = error && error.response && error.response.data
  if (data && data.message) return String(data.message)
  return (error && error.message) || 'Something went wrong'
}

/**
 * "Replay" for a payment whose money arrived but could not be applied to the policy, for example because the policy was
 * lapsed at the time and can now be reinstated. Shown only to people who may replay and only for such payments.
 * props: payment, onReplayed()
 */
export default function ReplayPayment({ payment, onReplayed }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(false)
  const [reason, setReason] = useState('')

  if (!canReplayPayment(payment) || !hasAuthourity('PREMIUM_UPDATE')) {
    return sent ? (
      <Typography variant="caption" color="text.secondary">
        Sent again. The status updates in a few seconds.
      </Typography>
    ) : null
  }

  const send = () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(
        `Please give a reason of at least ${MIN_REASON_LENGTH} characters`
      )
      return
    }
    setBusy(true)
    setError(null)
    replayPayment({ paymentGuid: payment.guid, reason: reason.trim() }).then(
      () => {
        setBusy(false)
        setOpen(false)
        setSent(true)
        // The policy system answers asynchronously, usually within seconds.
        setTimeout(onReplayed, 4000)
      },
      e => {
        setBusy(false)
        setError(messageOf(e))
      }
    )
  }

  const earlier = replayCount(payment)

  return (
    <div>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ReplayIcon />}
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        Replay
      </Button>
      <Dialog
        open={open}
        onClose={busy ? undefined : () => setOpen(false)}
        fullWidth
      >
        <DialogTitle>Replay this payment</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The money for this payment was received, but it could not be applied
            to the policy: {replayReason(payment)}.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Replaying sends it to the policy system again. If the policy can now
            take it (for example it has been reinstated, or can be reinstated by
            this payment), it is applied. If not, it fails again with the
            reason. A payment that has already been applied is never applied
            twice.
          </Typography>
          {earlier > 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              This payment has been replayed {earlier} time
              {earlier === 1 ? '' : 's'} before.
            </Alert>
          ) : null}
          <TextField
            label="Reason"
            helperText="Kept on the payment for audit, for example: policy reinstated, money confirmed with the partner"
            value={reason}
            onChange={e => setReason(e.target.value)}
            disabled={busy}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={send} disabled={busy}>
            Replay payment
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
